import { BaseFilterComponent } from './types';
import { defineMessages, useIntl } from 'react-intl';
import { Text } from '@lifeomic/chroma-react/components/Text';
import * as React from 'react';
import { Filter } from '../../types';
import { CSVTextEntry } from './CSVTextEntry';
import { NumericRange } from './NumericRange';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Divider } from '@lifeomic/chroma-react/components/Divider';

const messages = defineMessages({
  filter: {
    id: 'omicsExplorer.filters.variantQuality.filter',
    defaultMessage: 'VCF Filter',
  },
  caption: {
    id: 'omicsExplorer.filters.variantQuality.caption',
    defaultMessage: 'Input value(s) and press enter',
  },
});

const useStyles = makeStyles(theme => ({
  spacing: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  label: {
    marginBottom: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
  caption: {
    fontWeight: theme.typography.fontWeightBold,
    padding: theme.spacing(0.25),
    marginTop: theme.spacing(0.5),
  },
}));

const handleFilterAdd =
  (label: any, onFilterAdd: any) => (option: Filter | Array<Filter>) => {
    const updated = (Array.isArray(option) ? option : [option]).map(u => {
      u.origin = `${u.origin} / ${label}`;
      return u;
    });

    onFilterAdd(updated);
  };

export const VariantQuality: React.FC<BaseFilterComponent> = ({
  onFilterAdd,
  onFilterRemove,
  origin,
  selectedFilterOptions,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  return (
    <>
      <Text className={classes.label} size="table">
        {formatMessage(messages.filter)}
      </Text>
      <CSVTextEntry
        id="omicsExplorer.filters.variantQuality.filter"
        title={{
          id: 'omicsExplorer.filters.variantQuality.filter',
          defaultMessage: 'Filter',
        }}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        content={null}
        injectedProps={{
          searchKey: 'filter',
          textEntryOption: {
            id: 'omicsExplorer.filters.variantQuality.filter',
            searchValue: '',
            placeholderIntlLabel: {
              id: 'omicsExplorer.filters.variantQuality.filter.placeholderLabel',
              defaultMessage: 'Add (comma-separated)',
            },
          },
        }}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onFilterAdd={handleFilterAdd('Filter', onFilterAdd)}
        onFilterRemove={onFilterRemove}
        origin={origin}
        selectedFilterOptions={selectedFilterOptions}
      />
      <Text size="caption" className={classes.caption}>
        {formatMessage(messages.caption)}
      </Text>
      <Divider className={classes.spacing} />
      <NumericRange
        id="omicsExplorer.filters.variantQuality.quality"
        title={{
          id: 'omicsExplorer.filters.variantQuality.quality',
          defaultMessage: 'Quality',
        }}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        content={null}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onFilterAdd={handleFilterAdd('Quality', onFilterAdd)}
        onFilterRemove={onFilterRemove}
        origin={origin}
        selectedFilterOptions={selectedFilterOptions}
        injectedProps={{
          id: 'quality',
          searchKey: 'quality',
          step: 1.0,
          defaultMinimumValue: 30,
          maxHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.quality.maxHelpMessage',
            defaultMessage: '<= Max',
          },
          minHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.quality.minHelpMessage',
            defaultMessage: '>= Min',
          },
          buttonIntlLabel: {
            id: 'omicsExplorer.filters.variantQuality.quality.button.placeholder',
            defaultMessage: 'Add Quality Filter',
          },
        }}
      />
      <Divider className={classes.spacing} />
      <NumericRange
        id="omicsExplorer.filters.variantQuality.altReadDepth"
        title={{
          id: 'omicsExplorer.filters.variantQuality.altReadDepth',
          defaultMessage: 'Quality',
        }}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        content={null}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onFilterAdd={handleFilterAdd('Variant Read Depth', onFilterAdd)}
        onFilterRemove={onFilterRemove}
        origin={origin}
        selectedFilterOptions={selectedFilterOptions}
        injectedProps={{
          id: 'altReadDepth',
          searchKey: 'altReadDepth',
          step: 1.0,
          defaultMinimumValue: 3,
          maxHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.altReadDepth.maxHelpMessage',
            defaultMessage: '<= Max',
          },
          minHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.altReadDepth.minHelpMessage',
            defaultMessage: '>= Min',
          },
          buttonIntlLabel: {
            id: 'omicsExplorer.filters.variantQuality.altReadDepth.button.placeholder',
            defaultMessage: 'Add Variant Read Depth Filter',
          },
        }}
      />
      <Divider className={classes.spacing} />
      <NumericRange
        id="omicsExplorer.filters.variantQuality.readDepth"
        title={{
          id: 'omicsExplorer.filters.variantQuality.readDepth',
          defaultMessage: 'Quality',
        }}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        content={null}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        onFilterAdd={handleFilterAdd('Read Depth', onFilterAdd)}
        onFilterRemove={onFilterRemove}
        origin={origin}
        selectedFilterOptions={selectedFilterOptions}
        injectedProps={{
          id: 'readDepth',
          searchKey: 'readDepth',
          step: 1.0,
          defaultMinimumValue: 10,
          maxHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.readDepth.maxHelpMessage',
            defaultMessage: '<= Max',
          },
          minHelpMessage: {
            id: 'omicsExplorer.filters.variantQuality.readDepth.minHelpMessage',
            defaultMessage: '>= Min',
          },
          buttonIntlLabel: {
            id: 'omicsExplorer.filters.variantQuality.readDepth.button.placeholder',
            defaultMessage: 'Add Total Read Depth Filter',
          },
        }}
      />
    </>
  );
};
